import React, { useState, useEffect, useMemo, FC } from "react";
import { X, Download, Film, Clock, HardDrive, Loader2, CheckCircle2, AlertCircle, Video, Music, Zap } from "lucide-react";

/**
 * ExportAlphaDialog — Clean export dialog inspired by LVMH_PRO alpha.
 *
 * Uses the existing main export pipeline (onStartExport) but wraps it
 * in a proper modal dialog with settings, progress tracking and preview.
 */

type DialogView = "settings" | "progress" | "complete" | "error";

interface ExportAlphaDialogProps {
  open: boolean;
  onClose: () => void;
  /** Trigger the main export pipeline */
  onStartExport: (options: {
    mode: string;
    format: string;
    compression: string;
    extension: string;
    codec: string;
    burnSubtitles?: boolean;
    strategy?: string;
  }) => void;
  /** Cancel the current export */
  onCancelExport: () => void;
  /** Current export state from parent */
  isExporting: boolean;
  exportProgress: number;
  exportStatus: string;
  /** Latest exported video (set when export completes) */
  exportedVideos: {
    id: string;
    name: string;
    urlWebM?: string;
    urlMP4?: string;
    thumbnailUrl?: string;
    srtContent?: string;
  }[];
  totalTimelineDuration: number;
}

function formatTime(seconds: number): string {
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.round(seconds % 60);
  return `${minutes}m ${remainingSeconds}s`;
}

const ExportAlphaDialog: FC<ExportAlphaDialogProps> = ({
  open,
  onClose,
  onStartExport,
  onCancelExport,
  isExporting,
  exportProgress,
  exportStatus,
  exportedVideos,
  totalTimelineDuration,
}) => {
  const [view, setView] = useState<DialogView>("settings");
  const [quality, setQuality] = useState<"low" | "medium" | "high" | "ultra">("high");
  const [burnSubtitles, setBurnSubtitles] = useState(false);
  const [startTime, setStartTime] = useState<number | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [completedVideoUrl, setCompletedVideoUrl] = useState<string | null>(null);

  // Track elapsed time during export
  useEffect(() => {
    if (view === "progress" && !startTime) {
      setStartTime(Date.now());
    }
    if (view === "settings") {
      setStartTime(null);
      setElapsedSeconds(0);
    }
  }, [view, startTime]);

  useEffect(() => {
    if (!startTime || view !== "progress") return;
    const interval = setInterval(() => {
      setElapsedSeconds(Math.floor((Date.now() - startTime) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, [startTime, view]);

  // Watch for export completion
  useEffect(() => {
    if (view === "progress" && !isExporting && exportProgress >= 100) {
      // Export finished
      const latestVideo = exportedVideos[exportedVideos.length - 1];
      if (latestVideo) {
        setCompletedVideoUrl(latestVideo.urlMP4 || latestVideo.urlWebM || null);
      }
      setView("complete");
    }
  }, [isExporting, exportProgress, view, exportedVideos]);

  // Watch for export errors
  useEffect(() => {
    if (view === "progress" && !isExporting && exportProgress < 100 && exportProgress > 0) {
      setView("error");
    }
  }, [isExporting, exportProgress, view]);

  // Reset on close
  useEffect(() => {
    if (!open) {
      setView("settings");
      setStartTime(null);
      setElapsedSeconds(0);
      setCompletedVideoUrl(null);
    }
  }, [open]);

  const handleStartExport = () => {
    setView("progress");
    onStartExport({
      mode: "soft",
      format: "mp4",
      compression: "none",
      extension: ".mp4",
      codec: "copy",
      burnSubtitles,
      strategy: "auto",
    });
  };

  const handleCancel = () => {
    onCancelExport();
    setView("settings");
  };

  const handleDownload = () => {
    const latestVideo = exportedVideos[exportedVideos.length - 1];
    if (!latestVideo) return;
    const url = latestVideo.urlMP4 || latestVideo.urlWebM;
    if (!url) return;
    const a = document.createElement("a");
    a.href = url;
    a.download = `${latestVideo.name || "export"}.mp4`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  if (!open) return null;

  const preventClose = view === "progress";

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={preventClose ? undefined : onClose}
      />

      {/* Dialog */}
      <div className={`relative bg-white rounded-2xl shadow-2xl w-full mx-4 overflow-hidden ${view === "complete" && completedVideoUrl ? "max-w-[640px]" : "max-w-[480px]"}`}>

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              {view === "settings" && (
                <>
                  <Zap className="w-5 h-5 text-purple-600" />
                  Export (Alpha)
                </>
              )}
              {view === "progress" && (
                <>
                  <Loader2 className="w-5 h-5 text-purple-600 animate-spin" />
                  Export en cours...
                </>
              )}
              {view === "complete" && (
                <>
                  <CheckCircle2 className="w-5 h-5 text-green-500" />
                  Export terminé !
                </>
              )}
              {view === "error" && (
                <>
                  <AlertCircle className="w-5 h-5 text-red-500" />
                  Erreur d'export
                </>
              )}
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              {view === "settings" && "WebCodecs local — rapide et fiable"}
              {view === "progress" && "Rendu en cours, gardez l'onglet ouvert"}
              {view === "complete" && "Votre vidéo est prête"}
              {view === "error" && "Une erreur est survenue"}
            </p>
          </div>
          {!preventClose && (
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
              <X className="w-4 h-4 text-gray-500" />
            </button>
          )}
        </div>

        {/* Content */}
        <div className="px-6 py-5">

          {/* ===== Settings View ===== */}
          {view === "settings" && (
            <div className="space-y-5">

              {/* Duration info */}
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                <Film className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <div className="text-sm">
                  <span className="text-gray-500">Durée :</span>{" "}
                  <span className="font-medium text-gray-900">{formatTime(totalTimelineDuration)}</span>
                </div>
              </div>

              {/* Quality */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Qualité</label>
                <div className="grid grid-cols-4 gap-2">
                  {(["low", "medium", "high", "ultra"] as const).map((q) => (
                    <button
                      key={q}
                      onClick={() => setQuality(q)}
                      className={`px-3 py-2 text-xs font-medium rounded-lg border transition-all ${
                        quality === q
                          ? "bg-purple-600 text-white border-purple-600 shadow-sm"
                          : "bg-white text-gray-600 border-gray-200 hover:border-purple-300 hover:text-purple-600"
                      }`}
                    >
                      {q === "low" && "Basse"}
                      {q === "medium" && "Moyenne"}
                      {q === "high" && "Haute"}
                      {q === "ultra" && "Ultra"}
                    </button>
                  ))}
                </div>
                <p className="text-[10px] text-gray-400 mt-1">
                  {quality === "low" && "Plus rapide, fichier léger"}
                  {quality === "medium" && "Bon compromis taille/qualité"}
                  {quality === "high" && "Recommandé — qualité optimale"}
                  {quality === "ultra" && "Maximum — fichier plus lourd"}
                </p>
              </div>

              {/* Burn subtitles toggle */}
              <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100 transition-colors">
                <input
                  type="checkbox"
                  checked={burnSubtitles}
                  onChange={(e) => setBurnSubtitles(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                />
                <div>
                  <span className="text-sm font-medium text-gray-700">Incruster les sous-titres</span>
                  <span className="block text-[10px] text-gray-500 mt-0.5">
                    {burnSubtitles ? "Gravés dans l'image" : "Piste séparée (désactivable)"}
                  </span>
                </div>
              </label>

              {/* Format info */}
              <div className="flex items-center gap-3 p-3 bg-purple-50 rounded-xl">
                <Video className="w-4 h-4 text-purple-500 flex-shrink-0" />
                <div className="text-sm">
                  <span className="text-purple-700 font-medium">MP4 · H.264</span>
                  <span className="text-purple-500 ml-2 text-xs">Compatible partout</span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <button
                  onClick={onClose}
                  className="flex-1 px-4 py-3 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={handleStartExport}
                  disabled={!totalTimelineDuration}
                  className="flex-1 px-4 py-3 text-sm font-bold text-white bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 rounded-xl transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  Exporter
                </button>
              </div>
            </div>
          )}

          {/* ===== Progress View ===== */}
          {view === "progress" && (
            <div className="space-y-5">
              {/* Progress bar */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-gray-500">{exportStatus}</span>
                  <span className="text-sm font-semibold text-gray-900 tabular-nums">
                    {Math.round(exportProgress)}%
                  </span>
                </div>
                <div className="w-full h-2.5 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-purple-600 to-indigo-500 rounded-full transition-all duration-300 ease-out"
                    style={{ width: `${exportProgress}%` }}
                  />
                </div>
              </div>

              {/* Stats */}
              <div className="flex gap-6">
                {elapsedSeconds > 0 && (
                  <div className="flex items-center gap-2 text-sm">
                    <Clock className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-500">Temps :</span>
                    <span className="font-medium text-gray-900 tabular-nums">{formatTime(elapsedSeconds)}</span>
                  </div>
                )}
              </div>

              <p className="text-xs text-gray-400">
                Gardez cet onglet ouvert pendant le rendu.
              </p>

              <div className="flex justify-end">
                <button
                  onClick={handleCancel}
                  className="px-4 py-2.5 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-xl transition-colors"
                >
                  Annuler l'export
                </button>
              </div>
            </div>
          )}

          {/* ===== Complete View ===== */}
          {view === "complete" && (
            <div className="space-y-5">
              {/* Video preview */}
              {completedVideoUrl && (
                <div className="rounded-xl overflow-hidden bg-black">
                  <video
                    src={completedVideoUrl}
                    controls
                    className="w-full max-h-[300px]"
                    preload="metadata"
                  />
                </div>
              )}

              {/* Success banner */}
              <div className="flex items-center gap-3 p-3 bg-green-50 rounded-xl border border-green-200">
                <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
                <span className="text-sm text-green-700 font-medium">
                  Vidéo exportée avec succès !
                </span>
              </div>

              {/* Stats */}
              <div className="flex gap-6">
                {elapsedSeconds > 0 && (
                  <div className="flex items-center gap-2 text-sm">
                    <Clock className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-500">Durée :</span>
                    <span className="font-medium">{formatTime(elapsedSeconds)}</span>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <button
                  onClick={onClose}
                  className="flex-1 px-4 py-3 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
                >
                  Fermer
                </button>
                <button
                  onClick={handleDownload}
                  className="flex-1 px-4 py-3 text-sm font-bold text-white bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 rounded-xl transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  Télécharger
                </button>
              </div>
            </div>
          )}

          {/* ===== Error View ===== */}
          {view === "error" && (
            <div className="space-y-5">
              <div className="flex items-center gap-3 p-3 bg-red-50 rounded-xl border border-red-200">
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                <span className="text-sm text-red-700">
                  L'export a échoué. Réessayez ou utilisez un autre mode d'export.
                </span>
              </div>
              <div className="flex justify-end">
                <button
                  onClick={() => setView("settings")}
                  className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
                >
                  Réessayer
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExportAlphaDialog;
